import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { 
  DollarSign, Users, Check, RefreshCw, TrendingUp, Zap,
  Wallet, Gift, ArrowUpRight, Clock, CheckCircle, Crown, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RetentionBonusDashboard = ({ isOwner = false }) => {
  const [stats, setStats] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositNotes, setDepositNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, pendingRes] = await Promise.all([
        fetch(`${API_URL}/api/retention-bonus/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/retention-bonus/pending-approvals`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingApprovals(data.pending_by_user || []);
      }
    } catch (error) {
      console.error('Failed to fetch retention data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApproveWeekly = async (userId) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/retention-bonus/approve-weekly/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to approve bonuses');
      }
    } catch (error) {
      toast.error('Failed to approve bonuses');
    } finally {
      setProcessing(false);
    }
  };

  const handleDirectDeposit = async () => {
    if (!selectedUser || !depositAmount) return;

    const amount = parseFloat(depositAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/retention-bonus/owner-deposit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          amount,
          type: 'manual',
          notes: depositNotes
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setDepositDialogOpen(false);
        setSelectedUser(null);
        setDepositAmount('');
        setDepositNotes('');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to deposit bonus');
      }
    } catch (error) {
      toast.error('Failed to deposit bonus');
    } finally {
      setProcessing(false);
    }
  };

  const processDailyDeposits = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/retention-bonus/process-daily-deposits`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Processed ${data.processed_users} daily deposits totaling $${data.total_deposited.toFixed(2)}`);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to process deposits');
      }
    } catch (error) {
      toast.error('Failed to process deposits');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="retention-bonus-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Gift className="h-6 w-6 text-purple-600" />
            Retention Bonus Management
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage provider bonuses (0.05%-0.1% of 5-star deliveries)
          </p>
        </div>
        
        {isOwner && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={processDailyDeposits}
              disabled={processing}
              data-testid="process-daily-deposits-btn"
            >
              <Zap className="h-4 w-4 mr-2" />
              Process Daily Deposits
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-yellow-700 font-medium">Pending</p>
                  <p className="text-2xl font-bold text-yellow-900">
                    ${stats.pending?.amount?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-yellow-600">{stats.pending?.count || 0} bonuses</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-700 font-medium">Auto-Approved</p>
                  <p className="text-2xl font-bold text-blue-900">
                    ${stats.auto_approved?.amount?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-blue-600">{stats.auto_approved?.count || 0} bonuses</p>
                </div>
                <Zap className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-700 font-medium">Deposited</p>
                  <p className="text-2xl font-bold text-green-900">
                    ${stats.deposited?.amount?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-green-600">{stats.deposited?.count || 0} bonuses</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-purple-700 font-medium">Tips Distributed</p>
                  <p className="text-2xl font-bold text-purple-900">
                    ${stats.total_tips_distributed?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-purple-600">100% to providers</p>
                </div>
                <Wallet className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900">Bonus Rate: 0.05% - 0.1%</p>
                <p className="text-sm text-blue-700">
                  Providers earn a percentage of each delivery with a 5-star review.
                  Rate increases with experience and consistent high ratings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <DollarSign className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-semibold text-green-900">100% Tips Kept</p>
                <p className="text-sm text-green-700">
                  All tips from past jobs go directly to providers with zero platform fees.
                  Tips are deposited daily, fee-free.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Mechanic Approvals */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-600" />
            Pending Weekly Approvals (Mechanics)
            {pendingApprovals.length > 0 && (
              <Badge className="bg-yellow-100 text-yellow-800">{pendingApprovals.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Mechanic bonuses are tracked silently and paid weekly after admin approval
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingApprovals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto text-green-400 mb-3" />
              <p className="font-medium">All caught up!</p>
              <p className="text-sm">No pending mechanic bonuses to approve</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingApprovals.map((userGroup) => (
                <Card 
                  key={userGroup.user_id}
                  className="border-l-4 border-l-yellow-500 bg-yellow-50/30"
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                          <Users className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div>
                          <p className="font-semibold">{userGroup.user_name}</p>
                          <p className="text-sm text-gray-500">
                            {userGroup.bonuses?.length || 0} bonuses pending
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-600">
                            ${userGroup.total_amount?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-xs text-gray-400">Total amount</p>
                        </div>
                        
                        <div className="flex gap-2">
                          {isOwner && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(userGroup);
                                setDepositDialogOpen(true);
                              }}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Manual
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => handleApproveWeekly(userGroup.user_id)}
                            disabled={processing}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Owner Direct Deposit Dialog */}
      <Dialog open={depositDialogOpen} onOpenChange={setDepositDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              Owner Direct Deposit
            </DialogTitle>
            <DialogDescription>
              Manually deposit a bonus to {selectedUser?.user_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-700">Recipient</p>
              <p className="font-semibold text-purple-900">{selectedUser?.user_name}</p>
              <p className="text-xs text-purple-600">
                Auto-calculated: ${selectedUser?.total_amount?.toFixed(2) || '0.00'}
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Amount ($)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                placeholder={selectedUser?.total_amount?.toFixed(2) || "Enter amount..."}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Notes (Optional)</label>
              <Textarea
                value={depositNotes}
                onChange={(e) => setDepositNotes(e.target.value)}
                placeholder="Add notes..."
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDepositDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDirectDeposit}
              disabled={processing || !depositAmount}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {processing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Deposit Bonus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RetentionBonusDashboard;
